import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from "dayjs";
import { RiArrowDownSLine, RiFileDownloadLine, RiFileTextLine, RiCalculatorLine, RiCloseLine } from 'react-icons/ri';
import axios from 'axios';
import SearchInput from '../../../utils/SearchInput';
import viewIcon from "../../../assets/admin/view.svg";
import searchIcon from "../../../assets/admin/search.svg";
import TableLoader from '../../../utils/TableLoader';
import PaginationComponent from '../../../utils/PaginationComponent';

const ProposalsManagement = () => {
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const [proposals, setProposals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filter States
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("Scegli Status");
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const statusOptions = ["Tutti", "PENDING", "ACCEPTED", "CANCELLED"];
    
    // Restaurant Dropdown States
    const [searchRestQuery, setSearchRestQuery] = useState("");
    const [showOption, setShowOption] = useState(false);
    const [filteredRestArray, setFilteredRestArray] = useState([]);
    const [selectedRestId, setSelectedRestId] = useState(null);
    const [isLoading2, setIsLoading2] = useState(false);

    // Pagination States
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // PDF Download Modal States
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [selectedProposalForPdf, setSelectedProposalForPdf] = useState(null);

    const handleDownloadPdf = (showTotals = false) => {
        if (!selectedProposalForPdf?.id) return;
        const endpoint = showTotals
            ? `${apiUrl}/api/v1/proposals/${selectedProposalForPdf.id}/pdf?show_totals=true`
            : `${apiUrl}/api/v1/proposals/${selectedProposalForPdf.id}/pdf`;

        window.open(endpoint, '_blank');
        setIsPdfModalOpen(false);
    };

    // 1. Fetch Proposals
    const fetchProposals = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: page,
                per_page: rowsPerPage
            };
            
            if (searchQuery) {
                params.search = searchQuery;
            }
            if (selectedStatus && selectedStatus !== "Scegli Status" && selectedStatus !== "Tutti") {
                params.status = selectedStatus;
            }
            if (selectedRestId) {
                params.restaurant_id = selectedRestId;
            }

            const response = await axios.get(`${apiUrl}/api/v1/proposals`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                params: params
            });
            setProposals(response.data?.data || []);
            setTotalPages(response.data?.total || 0);
        } catch (error) {
            console.error("Error fetching proposals:", error);
            setProposals([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchProposals();
        }, 500); // 500ms debounce for search typing
        
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchQuery, selectedStatus, selectedRestId]);

    // 2. Fetch Restaurants
    const getRestaurantData = async () => {
        setIsLoading2(true);
        try {
            const params = {
                pageNumber: 1,
                size: 25,
                search: searchRestQuery,
            };
            const response = await axios.get(`${apiUrl}/api/v1/restaurants`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFilteredRestArray(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading2(false);
        }
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            getRestaurantData();
        }, 300);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchRestQuery]);

    const handleRestSelection = (id) => {
        setSelectedRestId((prevId) => (prevId === id ? null : id));
        setPage(1);
    };

    return (
        <div className="w-full">
            <div className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-5 w-full">
                <h3 className="text-3xl font-bold mb-3 xl:mb-0 poppins-medium self-start">Proposals</h3>
                <div className="self-start flex items-center gap-4 md:flex-row flex-col sm:w-auto w-full">
                    <SearchInput
                        onSearchChange={(val) => {
                            setSearchQuery(val);
                            setPage(1);
                        }}
                        component="Proposals"
                        placeholder="Proposals"
                    />
                    <div className="w-full flex flex-col lg:flex-row gap-2 dropdown-container">
                        
                        {/* Status Dropdown */}
                        <div className="relative w-full min-w-[180px]">
                            <button
                                onClick={() => setIsStatusOpen(!isStatusOpen)}
                                className="rounded-full py-[5px] border-[2px] bg-[#fff] border-[#F2C3CA] inline-flex justify-between items-center w-full px-4 outline-none text-[#8B2433] font-medium shadow-sm relative z-40"
                            >
                                <span className="mr-2 flex items-center gap-3 poppins-regular capitalize">
                                    {selectedStatus === "Tutti" ? "Scegli Status" : selectedStatus.toLowerCase()}
                                </span>
                                <RiArrowDownSLine className={`w-5 h-5 text-[#8B2433] transition-transform ${isStatusOpen ? "rotate-180" : ""}`} />
                            </button>
                            
                            {/* Invisible overlay to close dropdown when clicking outside */}
                            {isStatusOpen && (
                                <div className="fixed inset-0 z-30" onClick={() => setIsStatusOpen(false)}></div>
                            )}

                            {isStatusOpen && (
                                <div className="absolute top-[110%] left-0 w-full bg-white border border-[#F2C3CA] rounded-2xl shadow-lg z-50 py-2 overflow-hidden">
                                    {statusOptions.map(option => (
                                        <div
                                            key={option}
                                            onClick={() => {
                                                setSelectedStatus(option);
                                                setPage(1);
                                                setIsStatusOpen(false);
                                            }}
                                            className="px-4 py-2 hover:bg-[#8B2433] hover:text-white cursor-pointer poppins-regular text-[#303030] capitalize transition-colors"
                                        >
                                            {option === "Tutti" ? "Tutti gli status" : option.toLowerCase()}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Restaurant Dropdown from AllOrderManagement */}
                        <div className="relative group w-full min-w-[200px]">
                            <button
                                className={`${showOption ? "border-b-0 rounded-tl-3xl rounded-tr-3xl" : "rounded-full"} py-[5px] border-[2px] bg-[#fff] border-[#F2C3CA] inline-flex justify-between items-center w-full px-4 outline-none text-[#8B2433] font-medium shadow-sm relative z-30`}
                                onClick={() => setShowOption(!showOption)}
                            >
                                <span className="mr-2 block poppins-regular truncate flex-1 text-left">
                                    {selectedRestId
                                        ? (() => {
                                            const selectedRestaurant = filteredRestArray.find((rest) => rest.id === selectedRestId);
                                            const companyName = selectedRestaurant?.company_name;
                                            const restaurantCode = selectedRestaurant?.code;

                                            if (companyName && companyName !== "null") {
                                                return companyName.length > 25
                                                    ? `${companyName.slice(0, 25)}...`
                                                    : companyName;
                                            } else if (restaurantCode) {
                                                return restaurantCode;
                                            } else {
                                                return "Scegli Ristorante";
                                            }
                                        })()
                                        : "Scegli Ristorante"}
                                </span>
                                <RiArrowDownSLine
                                    className={`transform transition-transform duration-300 flex-shrink-0 ${showOption ? "rotate-180" : ""}`}
                                    size={"24px"}
                                    color="#9f414f"
                                />
                            </button>

                            {/* Invisible overlay to close dropdown when clicking outside */}
                            {showOption && (
                                <div className="fixed inset-0 z-20" onClick={() => setShowOption(false)}></div>
                            )}

                            <div
                                className={`
                                    ${showOption ? "opacity-100 scale-100 visible z-50" : "opacity-0 scale-95 invisible z-20"}
                                    bg-[#fff] h-[200px] border-[#F2C3CA] overflow-y-scroll absolute pt-[9px] rounded-t-none 
                                    border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 
                                    w-full border-[2px] border-[#8B24335C] 
                                    transition-all duration-300 ease-in-out transform origin-top
                                `}
                            >
                                <div className="flex px-[10px] py-[5px] flex-row relative border rounded-[28px] bg-[#f6efef66] my-[5px] justify-around gap-x-2">
                                    <img
                                        src={searchIcon}
                                        className="block left-0 top-0 bottom-0 my-auto h-4 w-4"
                                        alt="Search"
                                    />
                                    <input
                                        type="text"
                                        className="block poppins-regular bg-transparent outline-none cursor-pointer w-full placeholder:text-sm" placeholder="Scegli Ristorante"
                                        value={searchRestQuery}
                                        onChange={(e) => setSearchRestQuery(e.target.value)}
                                    />
                                </div>
                                {isLoading2 ? (
                                    <div role="status" className="flex justify-center items-center h-full">
                                        <svg
                                            aria-hidden="true"
                                            className="w-8 h-8 text-gray-200 animate-spin fill-[#8B2433]"
                                            viewBox="0 0 100 101"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentFill"
                                            />
                                        </svg>
                                        <span className="sr-only poppins-medium">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        {filteredRestArray && filteredRestArray?.length > 0 ? (
                                            filteredRestArray?.map((rest) => (
                                                <label
                                                    htmlFor=""
                                                    key={rest?.id}
                                                    className="border-b border-[#8b24331f] px-0 py-2 flex align-center justify-between flex-row-reverse gap-[15px] filter_list_btn"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="restaurantSelection"
                                                        className="outline-none cursor-pointer flex-shrink-0"
                                                        checked={selectedRestId === rest?.id}
                                                        onChange={() => handleRestSelection(rest?.id)}
                                                    />
                                                    <span className="text-[#303030] rounded-md block truncate poppins-regular text-xs text-left flex-1 ml-1">
                                                        {rest?.company_name && rest?.company_name !== "null" ? rest?.company_name : rest?.code}
                                                    </span>
                                                </label>
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-sm">No Restaurants available.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        
                    </div>
                </div>
            </div>
            
            <div className="w-auto dash-table-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow overflow-x-auto">
                <table className="md:w-full w-[700px] text-left wine_table">
                    <thead>
                        <tr className="border-b border-[#8B2433]">
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Sr.no</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome Ristorante</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Data Creazione</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Stato</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Vini Totali</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Azione</th>
                        </tr>
                    </thead>
                    {isLoading ? (
                        <TableLoader />
                    ) : (
                        <tbody>
                            {proposals && proposals.length > 0 ? (
                                proposals.map((item, index) => (
                                    <tr key={item.id} className="border-b border-[#8B2433]/12">
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {(page - 1) * 10 + index + 1}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                             {item.restaurant?.company_name ? `${item.restaurant.company_name}` : ""}
                                            {/* {item.restaurant?.company_name ? `${item.restaurant.company_name} - ` : ""} */}
                                            {/* {item.restaurant?.code} */}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {dayjs(item.created_at).format("DD-MM-YYYY")}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {(() => {
                                                const s = item.status?.toUpperCase();
                                                let badgeClass = "bg-gray-50 text-gray-800 border-gray-200";
                                                if (s === "PENDING") {
                                                    badgeClass = "bg-amber-50 text-amber-800 border-amber-200";
                                                } else if (s === "ACCEPTED") {
                                                    badgeClass = "bg-emerald-50 text-emerald-800 border-emerald-200";
                                                } else if (s === "CANCELLED") {
                                                    badgeClass = "bg-rose-50 text-rose-800 border-rose-200";
                                                }
                                                return (
                                                    <span className={`inline-flex items-center justify-center px-2.5 py-[3px] text-[11px] font-medium tracking-wide uppercase rounded-md border ${badgeClass}`}>
                                                        {item.status || "N/A"}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{item.wines_count}</td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/proposals/${item.id}`)}
                                                    className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                    title="Vedi Dettagli Proposal"
                                                >
                                                    <img src={viewIcon} alt="view" className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedProposalForPdf(item);
                                                        setIsPdfModalOpen(true);
                                                    }}
                                                    className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                    title="Scarica Proposal PDF"
                                                >
                                                    <RiFileDownloadLine className="w-5 h-5 text-[#8B2433]" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-4">Nessuna proposal trovata</td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>
            {proposals && proposals.length > 0 && (
                <PaginationComponent
                    page={page}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                />
            )}

            {/* Premium Download PDF Options Modal */}
            {isPdfModalOpen && selectedProposalForPdf && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={() => setIsPdfModalOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl border border-[#8B2433]/20 w-full max-w-md overflow-hidden relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#8B2433] to-[#681723] p-5 text-white flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-white/10 rounded-xl">
                                    <RiFileDownloadLine className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold poppins-medium">Scarica Proposal PDF</h4>
                                    <p className="text-xs text-white/80 poppins-regular">Seleziona la modalità di esportazione</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsPdfModalOpen(false)}
                                className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
                            >
                                <RiCloseLine className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600 poppins-regular text-center mb-1">
                                Scegli quale formato di PDF desideri scaricare:
                            </p>

                            {/* Option 1: Without Totals */}
                            <button
                                onClick={() => handleDownloadPdf(false)}
                                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 hover:border-[#8B2433] hover:bg-[#8B2433]/5 hover:shadow-md transition-all duration-200 group text-left"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-xl bg-gray-100 group-hover:bg-[#8B2433] group-hover:text-white text-gray-700 transition-colors">
                                        <RiFileTextLine className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-gray-900 poppins-medium group-hover:text-[#8B2433] transition-colors">
                                            Opuscolo
                                        </h5>
                                        <p className="text-xs text-gray-500 poppins-regular">
                                            Genera il PDF standard con la tabella dei prezzi a scaglioni.
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-700 group-hover:bg-[#8B2433] group-hover:text-white transition-colors">
                                    PDF
                                </span>
                            </button>

                            {/* Option 2: With Totals */}
                            <button
                                onClick={() => handleDownloadPdf(true)}
                                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 hover:border-[#8B2433] hover:bg-[#8B2433]/5 hover:shadow-md transition-all duration-200 group text-left"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-xl bg-gray-100 group-hover:bg-[#8B2433] group-hover:text-white text-gray-700 transition-colors">
                                        <RiCalculatorLine className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-gray-900 poppins-medium group-hover:text-[#8B2433] transition-colors">
                                            Preventivo
                                        </h5>
                                        <p className="text-xs text-gray-500 poppins-regular">
                                            Genera il PDF che calcola i totali delle singole voci e il totale complessivo.
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-700 group-hover:bg-[#8B2433] group-hover:text-white transition-colors">
                                    PDF
                                </span>
                            </button>
                        </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => setIsPdfModalOpen(false)}
                                className="px-4 py-2 text-xs poppins-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProposalsManagement;
